import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {pagingOptions} from '../../../../../../../util/uiconstants';
import {useEngagementId} from '../../../../../../../util/customHooks';
import {getAccounts} from '../../../../../../../actions/Account/accountactions';
import {
	getAccountsForInherentRiskFactors,
	getAccountsPagingOptions
} from '../../../../../../../selectors/Ares/accounts';
import {labels} from '../../../../../../../util/utils';
import NoRowsOverlay from './NoRowsOverlay';
import AgGridWrapper from '../../../../../BaseComponentWrappers/AgGridWrapper';
import InherentRiskFactorsTableBottom from './InherentRiskFactorsTableBottom';
import AccountsAssertionCellRenderer from './AccountsAssertionCellRenderer';
import {findInherentRiskFactorTypes} from './accountSummaryUtilityFunctions';
import InherentRiskFactorTypesCellRenderer from './InherentRiskFactorTypesCellRenderer';

export default function ALRAInherentRiskFactors(props) {
	const {formActions} = props;
	const dispatch = useDispatch();

	// Default page size 50
	const [pageSize, setPageSize] = useState(pagingOptions.options[1]);
	const [pageNumber, setPageNumber] = useState(1);
	const [columnDefs, setColumnDefs] = useState([]);
	const engagementId = useEngagementId();
	const [searchString, setSearchString] = useState();
	const [searchStringOnEnter, setSearchStringOnEnter] = useState();

	const fetch = useCallback(
		() =>
			dispatch(
				getAccounts(engagementId, {
					searchString: searchStringOnEnter,
					orderBy: 'accountName',
					includeRelatedRisks: true,
					includePspIndexes: true,
					includeAssertions: true,
					includeHelix: true,
					page: pageNumber,
					pageSize: pageSize,
					includePagination: true,
					includeRelatedDocuments: true
				})
			),
		[pageNumber, pageSize, searchStringOnEnter]
	);

	const getDataRow = useSelector((state) => getAccountsForInherentRiskFactors(state, formActions));
	const accountsPagingOptions = useSelector((state) => getAccountsPagingOptions(state));
	const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
	const defaultColDef = useMemo(() => {
		return {
			minWidth: 50,
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true
		};
	}, []);
	const gridRef = useRef();
	const handleOnGridReady = useCallback((params) => {
		gridRef.current = params.api;
		const grids = document.querySelectorAll(`section.AGGridWrapperSection`);
		if (grids && grids.length > 0) {
			grids.forEach((grid) => {
				grid.querySelector('.ag-body-viewport').classList.add('customScrollbar');
			});
			resizeGrid(params);

			window.addEventListener('resize', function() {
				resizeGrid(params);
			});
		}
	}, []);

	const resizeGrid = (params) => {
		setTimeout(function() {
			params.api.sizeColumnsToFit();
		}, 500);
	};

	const defineColumnDefs = () => {
		const columnDefs = [
			{
				headerName: `${labels.ALRAAccountDisclosure}`,
				cellRenderer: 'agGroupCellRenderer',
				showRowGroup: true,
				minWidth: 150,
				cellRendererParams: {
					suppressCount: true,
					innerRenderer: AccountsAssertionCellRenderer
				},
				onCellClicked: (params) => {
					if (
						(params.node.level === 0 &&
							(params.event.target.querySelector('span.motif-tooltip-trigger-wrap') ||
								params.event.target.querySelector('section.StyledAccountNameIsLink') ||
								params.event.target.querySelector('section.StyledCreate') ||
								(params.event.target.firstElementChild &&
									params.event.target.firstElementChild.tagName === 'SPAN' &&
									params.event.target.firstElementChild.classList.contains('account-name')))) ||
						(params.node.level === 1 && params.event.target.closest('.StyledRelatedAssertions'))
					) {
						// update the node to be expanded
						params.node.expanded = !params.node.expanded;
						// tell the grid to redraw based on state of nodes expanded state
						params.api.onGroupExpandedOrCollapsed(params.rowIndex);
					} else {
						params.event.stopPropagation();
					}
				}
			}
		];
		const newColumnDefs = findInherentRiskFactorTypes(columnDefs, formActions);

		setColumnDefs(newColumnDefs);
	};

	const noRowsOverlayComponent = useMemo(() => {
		return NoRowsOverlay;
	}, []);

	const getDataPath = useMemo(() => {
		return (data) => {
			let path = data?.path;
			if (path && path.length === 3) {
				if (path[1] === 'NO_RISKS_ASSOCIATED') {
					path[1] = `${labels.ALRANoRisksAssociated}.`;
				}
				if (path[1] === 'LIMITED_RISK_ACCOUNT') {
					path[1] = `${labels.ALRALimitedRiskAccount}.`;
				}
				if (path[1] === 'INSIGNIFICANT_RISK_ACCOUNT') {
					path[1] = `${labels.ALRAInsignificantRiskAccount}.`;
				}
			}
			return path;
		};
	}, []);

	const groupDisplayType = 'custom';

	useEffect(() => {
		defineColumnDefs();
	}, []);

	const handleOnPageSizeChanged = useCallback((_pageSize) => {
		setPageSize(_pageSize);
		setPageNumber(1);
	}, []);

	const handleOnPageChanged = useCallback((pageNumber) => {
		setPageNumber(pageNumber);
	}, []);

	const searchAccounts = (e) => {
		setSearchStringOnEnter(searchString);
	};

	const onChangeSearchText = (event) => {
		setSearchString(event.target.value);
	};

	const onClear = () => {
		setSearchString('');
		setSearchStringOnEnter('');
	};

	return (
		<>
			<StyledALRAInherentRiskFactors className="StyledALRAInherentRiskFactors">
				<section style={containerStyle} className="AGGridWrapperSection">
					<AgGridWrapper
						rowData={getDataRow}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						animateRows
						groupDisplayType={groupDisplayType}
						noRowsOverlayComponent={noRowsOverlayComponent}
						suppressRowClickSelection
						suppressCellFocus
						wrapHeaderText
						autoHeaderHeight
						suppressContextMenu
						onReady={handleOnGridReady}
						domLayout="normal"
						getDataPath={getDataPath}
						treeData
						components={{
							inherentRiskFactorTypesCellRenderer: InherentRiskFactorTypesCellRenderer
						}}
					/>
				</section>
			</StyledALRAInherentRiskFactors>
			<InherentRiskFactorsTableBottom />
		</>
	);
}

const StyledALRAInherentRiskFactors = styled.section`
	.AGGridWrapperSection {
		.ag-root-wrapper {
			&.ag-layout-normal {
				border-radius: var(--px-8) var(--px-8) 0 0 !important;
			}
		}
		.ag-ltr {
			.ag-row-group-indent-0,
			.ag-row-group-indent-1 {
				align-items: center;
				.ag-group-expanded,
				.ag-group-contracted {
					min-height: var(--px-22);
					height: auto;
					margin-right: var(--px-5);
					.ag-icon {
						&::before {
							filland.: var(--neutrals-900);
							color: var(--neutrals-900);
						}
					}
				}
			}
			.ag-row-group-indent-0 {
				.ag-group-value {
					width: 100%;
					.motif-tooltip-wrapper {
						display: flex;
						width: 100%;
						.motif-tooltip-trigger-wrap {
							width: auto;
							max-width: 100%;
						}
					}
					.link-for-drawer {
						width: 100%;
					}
					.StyledCreate {
						.StyledCreateLabel {
							font-weight: 400 !important;
						}
					}
				}
			}
			.ag-row-group-indent-1 {
				padding-left: 0;
				width: 100%;
				&.ag-cell-expandable {
					.ag-group-value {
						padding-left: 0;
					}
				}
				&.ag-row-group-leaf-indent {
					.ag-group-value {
						padding-left: var(--px-25);
					}
				}
				.ag-group-value {
					width: 100%;
				}
			}
			.ag-row-group-leaf-indent {
				margin-left: var(--px-24);
			}
		}
		.ag-header {
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
							.ag-header-cell-text {
								font-size: var(--px-14) !important;
								font-weight: 400 !important;
							}
							.ag-header-cell-label {
								.ag-header-cell-text {
									font-size: var(--px-14) !important;
									font-weight: 400 !important;
								}
							}
							&[col-id='accountName'] {
								padding-left: var(--px-24);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='accountTypeId'] {
								padding-left: var(--px-20);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='assertions'] {
								padding-left: var(--px-25);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='analysisperiod'] {
								padding-right: var(--px-20);
								.ag-header-cell-label {
									justify-content: flex-end !important;
								}
							}
							&[col-id='xTE'] {
								padding-right: var(--px-20);
								.ag-header-cell-label {
									justify-content: flex-end !important;
								}
							}
							&[col-id='0'] {
								padding-left: var(--px-24);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='1'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='2'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
								.ag-header-cell-label {
									.ag-header-cell-text {
										text-align: center;
										white-space: pre-wrap;
									}
								}
							}
							&[col-id='3'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
								.ag-header-cell-label {
									.ag-header-cell-text {
										text-align: center;
										white-space: pre-wrap;
									}
								}
							}
							&[col-id='4'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='5'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
								.ag-header-cell-label {
									.ag-header-cell-text {
										text-align: center;
										white-space: pre-wrap;
									}
								}
							}
							&[col-id='6'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
								.ag-header-cell-label {
									.ag-header-cell-text {
										text-align: center;
										white-space: pre-wrap;
									}
								}
							}
							&[col-id='7'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
								.ag-header-cell-label {
									.ag-header-cell-text {
										text-align: center;
										white-space: pre-wrap;
									}
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			.ag-full-width-container {
				.ag-row {
					&.ag-row-level-3 {
						padding: 0 var(--px-110) !important;
					}
				}
			}
			.ag-row {
				&:last-child {
					border-bottom: 0;
				}
				&.ag-row-level-0 {
					.ag-cell {
						&[col-id='0'] {
							.ag-cell-value {
								font-size: var(--px-16);
								font-weight: 400;
								line-height: var(--px-24);
								color: #656579;
								padding-left: var(--px-10);
							}
						}
					}
				}
				&.ag-row-level-1 {
					.ag-cell {
						&[col-id='1'] {
							.ag-cell-value {
								font-size: var(--px-14);
								font-weight: 300;
								color: #656579;
							}
						}
						.ag-cell-value {
							.ag-row-group-indent-1 {
								margin-left: 0;
								padding-left: var(--px-24);
							}
						}
					}
				}
				&.ag-row-level-2 {
					.ag-cell {
						&[col-id='1'] {
							.ag-cell-value {
								font-size: var(--px-12);
								font-weight: 300;
								color: #656579;
							}
						}
						.ag-cell-value {
							.ag-row-group-indent-2 {
								margin-left: var(--px-48);
								padding-left: 0;
							}
						}
					}
				}
				.ag-cell {
					&[col-id='1'],
					&[col-id='2'],
					&[col-id='3'],
					&[col-id='4'],
					&[col-id='5'],
					&[col-id='6'] {
						display: inline-flex;
						align-items: center;
						width: 100%;
						text-align: center;
						color: var(--neutrals-900);
						border-left: var(--px-1) solid var(--neutrals-200);
						.ag-cell-wrapper {
							width: 100%;
							.ag-cell-value {
								width: 100%;
								display: inline-flex;
								align-items: center;
								justify-content: center;
								svg {
									width: var(--px-18);
									height: var(--px-18);
									color: var(--neutrals-900);
								}
								.risk {
									background-color: #fff;
									color: #656579;
									border-radius: var(--px-4);
									font-size: var(--px-14);
									font-weight: 400;
									line-height: var(--px-20);
									display: inline-flex;
									justify-content: center;
									align-items: center;
									width: var(--px-28);
									height: var(--px-28);
									&.high-risk {
										border: var(--px-1) solid #b14891;
										background-color: #b14891;
										color: #fff;
										.motif-icon {
											svg {
												color: #fff;
											}
										}
									}
									&.low-risk {
										border: var(--px-2) solid #b14891;
										background: var(--neutrals-00white);
										color: var(--neutrals-1000);
										.motif-icon {
											svg {
												color: var(--neutrals-1000);
											}
										}
									}
								}
							}
						}
					}
					&[col-id='accountName'] {
						padding-left: var(--px-24);
						color: var(--neutrals-900);
						text-align: left;
						display: inline-flex;
						align-items: center;
						width: 100%;
						.ag-cell-wrapper {
							width: 100%;
							.ag-cell-value {
								width: 100%;
							}
						}
					}
					&[col-id='accountTypeId'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-left: var(--px-20);
						display: inline-flex;
						align-items: center;
					}
					&[col-id='assertions'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-left: var(--px-20);
						display: inline-flex;
						align-items: center;
					}
					.ag-cell-value {
						.assertion-item {
							display: inline-flex;
							justify-content: center;
							align-items: center;
							margin: 0 !important;
							.craBadgesWrapper {
								margin: 0 !important;
								.assertion {
									margin: 0 !important;
								}
							}
						}
					}
				}
			}
		}
	}
`;
